/**
 *  @class
 *  @function FacetsToggle
 */
class FacetToggle extends HTMLElement {

  constructor() {
    super();
    this.init();
  }
  init() {
    this.drawer = document.getElementById('Facet-Drawer');
    this.container = this.closest('.facets--bar-sticky');
    this.button = this.querySelector('button');

    // Add functionality to button
    if (this.button) {
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpening = !this.drawer.classList.contains('active');
        document.body.classList.toggle('open-cc');
        this.drawer.classList.toggle('active');
        
        if (isOpening) {
          // Setup handlers before enabling focus trap
          if (!this.handlersSetup) {
            this.setupCloseHandlers();
            this.handlersSetup = true;
          }
          this.enableFocusTrap();
        } else {
          this.disableFocusTrap();
        }
      });
    }
    if (this.container) {
      this.enableSticky();
    }
  }
  
  setupCloseHandlers() {
    // Close button handler
    const closeButton = this.drawer?.querySelector('.side-panel-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.disableFocusTrap();
      });
    }

    // Escape key handler
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('active')) {
        e.preventDefault();
        document.body.classList.remove('open-cc');
        this.drawer.classList.remove('active');
        this.disableFocusTrap();
        if (this.button) {
          this.button.focus(); // Return focus to the toggle button
        }
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  getFocusableElements() {
    if (!this.drawer) return [];
    
    const focusableSelectors = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'summary',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    return Array.from(this.drawer.querySelectorAll(focusableSelectors))
      .filter(el => {
        // Check if element is visible
        if (el.offsetParent === null) return false;
        if (getComputedStyle(el).visibility === 'hidden') return false;
        if (getComputedStyle(el).display === 'none') return false;
        
        // Check if element is inside a closed details element
        const details = el.closest('details');
        if (details && !details.open && el !== details.querySelector('summary')) {
          return false;
        }
        
        // Try to focus the element to verify it's actually focusable
        return true;
      });
  }

  enableFocusTrap() {
    if (!this.drawer) {
      console.warn('Facet drawer not found');
      return;
    }

    // Make drawer focusable if it isn't already
    if (!this.drawer.hasAttribute('tabindex')) {
      this.drawer.setAttribute('tabindex', '-1');
    }

    // Create and store the handler - attach to document to catch all tab events
    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;
      
      // Only trap if drawer is active
      if (!this.drawer.classList.contains('active')) {
        return;
      }

      const focusableElements = this.getFocusableElements();
      
      if (focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const isInDrawer = this.drawer.contains(document.activeElement);

      if (e.shiftKey) {
        // Shift + Tab (going backwards)
        if (document.activeElement === firstElement || !isInDrawer) {
          e.preventDefault();
          setTimeout(() => {
            console.log('Attempting to focus last element:', lastElement);
            lastElement.focus();
            console.log('After focus, activeElement is:', document.activeElement);
          }, 0);
        }
      } else {
        // Tab (going forwards)
        if (document.activeElement === lastElement || !isInDrawer) {
          e.preventDefault();
          setTimeout(() => {
            console.log('Attempting to focus first element:', firstElement);
            firstElement.focus();
            console.log('After focus, activeElement is:', document.activeElement);
          }, 0);
        }
      }
    };

    // Attach to document to catch all tab events - use capture phase to intercept early
    document.addEventListener('keydown', this.focusTrapHandler, true);

    // Focus the drawer first, then move to first interactive element
    // Use requestAnimationFrame for better timing with CSS transitions
    requestAnimationFrame(() => {
      setTimeout(() => {
        // First focus the drawer container itself
        this.drawer.focus();
        console.log('Focus set to drawer container');
        
        // Then immediately move to first focusable element
        setTimeout(() => {
          const focusableElements = this.getFocusableElements();
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
            console.log('Focus moved to first focusable element:', focusableElements[0]);
          } else {
            console.warn('No focusable elements found in drawer');
          }
        }, 50);
      }, 200);
    });
  }

  disableFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler, true);
      this.focusTrapHandler = null;
    }
  }

  enableSticky() {
    let rootMargin = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) + 1;
    this.observer = new IntersectionObserver(
      ([e]) => {
        e.target.classList.toggle('is-pinned', e.intersectionRatio <= 1 && e.intersectionRatio > 0);
      },
      {
        threshold: [1],
        rootMargin: `-${rootMargin}px 0px 0px 0px`
      }
    );

    this.observer.observe(this.container);
  }

}
customElements.define('facet-toggle', FacetToggle);

/**
 *  @class
 *  @function FacetFiltersForm
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      if (event.target.id === 'compare_toggle') {
        return;
      }
      this.onSubmitHandler(event);
    }, 500);

    const form = this.querySelector('form');
    form.addEventListener('input', this.debouncedOnSubmit.bind(this));
    
    // Allow Enter key to toggle checkboxes in facet filters
    form.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.type === 'checkbox') {
        event.preventDefault();
        event.target.checked = !event.target.checked;
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('collapsible-row') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('collapsible-row'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData);
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 *  @class
 *  @function Facetremove
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.compareToggle = this.querySelector('#compare_toggle');
    if (this.compareToggle) {
      this.compareToggle.addEventListener('change', this.onCompare);
    }
  }
  onCompare(e) {
    document.body.classList.toggle('compare-true');
    e.preventDefault();
    return false;
  }
}
customElements.define('facet-toolbar', FacetToolbar);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [
          { 'aria-label': 'lower' },
          { 'aria-label': 'upper' },
        ],
        range: {
          'min': 0,
          'max': parseFloat(slider.dataset.max)
        }
      },
      event = new CustomEvent('input'),
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });
    slider.noUiSlider.on('change', function (values) {
      form.querySelector('form').dispatchEvent(event);
    });
  }
}
customElements.define('price-slider', PriceSlider);